import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, map, startWith } from 'rxjs';

import { trackByFunctions } from '@assess/browser/track-by-functions';

@UntilDestroy()
@Component({
  selector: 'assess-string-filter',
  templateUrl: './string-filter.component.html',
  styleUrls: ['./string-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StringFilterComponent implements OnInit, OnChanges {
  @Input() label: string | null | undefined;
  @Input() placeholder: string | undefined = 'Any';
  @Input() strings: string[] | undefined = [];
  @Input() value: string[] | undefined = [];
  @Input() multiple: boolean | undefined = false;

  @Output()
  readonly selected = new EventEmitter<string[]>();

  readonly trackByIndex = trackByFunctions.byIndex;
  readonly stringSearch = new FormControl<string | string[]>('', { nonNullable: true });

  filteredStrings!: Observable<string[] | undefined>;
  stringSelection!: SelectionModel<string>;
  stringSelectionToggles: boolean[] = [];
  currentFilter = '';

  ngOnInit() {
    this.stringSelection = new SelectionModel<string>(this.multiple, []);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.strings) {
      if (this.stringSelection) {
        this.stringSelection.clear();
      }

      this.filteredStrings = this.stringSearch.valueChanges.pipe(
        startWith<string | string[]>(''),
        map((value) => (typeof value === 'string' ? value : this.currentFilter)),
        map((filter) => this.filterStrings(filter))
      );
    }

    if (changes.value && !changes.value.currentValue.length && !changes.value.firstChange) {
      this.clearStrings();
    }
  }

  filterStrings(filter: string) {
    this.currentFilter = filter;

    const filteredList = filter
      ? this.strings?.filter((option) => option.toLowerCase().includes(filter.toLowerCase()))
      : this.strings;

    this.setStringSelectionToggles(filteredList ?? []);

    return filteredList;
  }

  toggleAutocompletePanel(trigger: MatAutocompleteTrigger) {
    if (!this.multiple) {
      trigger.closePanel();
    }
  }

  displayStrings(selectedStrings: string[] | string) {
    return (Array.isArray(selectedStrings) ? selectedStrings.join('; ') : selectedStrings).toUpperCase();
  }

  selectString(event: Event, stringValue: string, index: number) {
    event.stopPropagation();
    this.toggleString(stringValue, index);
  }

  toggleString(stringValue: string, index: number) {
    this.stringSelection.toggle(stringValue);
    this.stringSelectionToggles[index] = this.stringSelection.isSelected(stringValue);
    this.stringSearch.setValue(this.stringSelection.isEmpty() ? '' : this.stringSelection.selected);
    this.selected.emit(this.stringSelection.selected);
  }

  clearStrings() {
    this.stringSelection.clear();
    this.stringSelectionToggles = [];
    this.stringSearch.setValue('');
    this.selected.emit([]);
  }

  setStringSelectionToggles(stringList: string[]) {
    this.stringSelectionToggles = [];

    stringList?.forEach((stringValue, index) => {
      this.stringSelectionToggles[index] = this.stringSelection.isSelected(stringValue);
    });
  }
}
